import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, FileCode, Clipboard, Eye, ArrowRight,
  AlertCircle, CheckCircle, AlertTriangle, Sparkles,
  X, RefreshCw, Shield, Zap, ExternalLink
} from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { useTranslation } from '../i18n/useTranslation';
import type { ValidationResult } from '../types';
import { clsx } from 'clsx';

// ─── Client-side mock validation ──────────────────────────────────────
function validateHtmlClient(html: string): ValidationResult {
  const warnings: string[] = [];
  const blockedItems: string[] = [];
  let xssFound = false;

  const scriptMatches = html.match(/<script[\s\S]*?<\/script>/gi);
  if (scriptMatches) {
    blockedItems.push(`${scriptMatches.length} tag <script> dikesan dan dibuang`);
    xssFound = true;
  }
  const onEventMatches = html.match(/on(click|load|error|mouseover|change|submit|focus|blur)=/gi);
  if (onEventMatches) {
    blockedItems.push(`${onEventMatches.length} event handler dikesan dan dibuang`);
    xssFound = true;
  }
  const httpLinks = html.match(/["']http:\/\//g);
  if (httpLinks) warnings.push(`${httpLinks.length} link HTTP akan di-upgrade ke HTTPS`);
  const jsUrls = html.match(/javascript:/gi);
  if (jsUrls) { blockedItems.push(`${jsUrls.length} javascript: URL dibuang`); xssFound = true; }
  const iframes = html.match(/<iframe[\s\S]*?>/gi);
  if (iframes) warnings.push(`${iframes.length} iframe dikesan — pastikan sumber dipercayai`);
  const cryptoMiner = /coinhive|cryptonight|minero|webminer/i.test(html);
  if (cryptoMiner) { blockedItems.push('Kod crypto miner dikesan!'); xssFound = true; }

  const sizeBytes = new Blob([html]).size;
  const maxSize = 512 * 1024;

  if (sizeBytes > maxSize) {
    return {
      passed: false,
      warnings, blockedItems,
      securityReport: { xssFound, linksScanned: 0, assetsScanned: 0, finalSizeBytes: sizeBytes },
      error: `Saiz HTML terlalu besar: ${Math.round(sizeBytes / 1024)}KB (had: 512KB untuk plan percuma)`,
    };
  }

  return {
    passed: true,
    cleanHtml: html,
    warnings, blockedItems,
    securityReport: {
      xssFound,
      linksScanned: (html.match(/href=/g) || []).length,
      assetsScanned: (html.match(/src=/g) || []).length,
      finalSizeBytes: sizeBytes,
    },
  };
}

// ─── Security Badge ───────────────────────────────────────────────────
function SecurityBadge({ result, t }: { result: ValidationResult; t: (k: any) => string }) {
  const hasBlocked = result.blockedItems.length > 0;
  const hasWarnings = result.warnings.length > 0;

  if (!result.passed) {
    return (
      <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
        <AlertCircle size={18} />
        <div>
          <div className="font-bold text-sm">{t('import.failed')}</div>
          <div className="text-xs">{result.error}</div>
        </div>
      </div>
    );
  }

  if (hasBlocked) {
    return (
      <div className="flex items-center gap-2 text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
        <AlertTriangle size={18} />
        <div>
          <div className="font-bold text-sm">{t('import.cleaned')}</div>
          <div className="text-xs">{result.blockedItems.length} {t('import.cleaned_sub')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
      <CheckCircle size={18} />
      <div>
        <div className="font-bold text-sm">{t('import.safe')}</div>
        <div className="text-xs">{t('import.safe_sub')}</div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────
export default function ImportPage() {
  const navigate = useNavigate();
  const { setCurrentHtml, setSelectedTemplate, setValidationResult, showToast } = useAppStore();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<'file' | 'paste'>('paste');
  const [htmlInput, setHtmlInput] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const runValidation = useCallback(async (html: string) => {
    if (!html.trim()) { setValidation(null); return; }
    setIsValidating(true);
    await new Promise((r) => setTimeout(r, 600)); // Simulate async
    const result = validateHtmlClient(html);
    setValidation(result);
    setValidationResult(result);
    setIsValidating(false);
  }, [setValidationResult]);

  const handleHtmlChange = (html: string) => {
    setHtmlInput(html);
    const timer = setTimeout(() => runValidation(html), 800);
    return () => clearTimeout(timer);
  };

  const handleFileRead = (file: File) => {
    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      showToast(t('import.err_file_type'), 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setHtmlInput(content);
      setFileName(file.name);
      runValidation(content);
      setActiveTab('paste');
      showToast(`"${file.name}" ${t('import.file_loaded')}`, 'success');
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileRead(file);
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.trim()) {
        setHtmlInput(text);
        runValidation(text);
        showToast(t('import.pasted'), 'success');
      } else {
        showToast(t('import.clipboard_empty'), 'info');
      }
    } catch {
      showToast(t('import.clipboard_err'), 'error');
    }
  };

  const handleProceed = () => {
    if (!htmlInput.trim()) {
      showToast(t('import.err_no_html'), 'error');
      return;
    }
    if (validation && !validation.passed) {
      showToast(t('import.err_security'), 'error');
      return;
    }
    setSelectedTemplate(null);
    setCurrentHtml(htmlInput);
    navigate('/editor');
  };

  const sizeKb = htmlInput ? Math.round(new Blob([htmlInput]).size / 1024) : 0;

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <div className="bg-white border-b border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Upload size={28} className="text-telegram" />
            <h1 className="text-3xl font-extrabold text-surface-900">{t('import.title')}</h1>
          </div>
          <p className="text-surface-500">{t('import.sub')}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">

          {/* ─── Left Panel: Input ─────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Tab Switcher */}
            <div className="flex bg-white rounded-xl border border-surface-200 p-1 gap-1 shadow-sm">
              <button
                onClick={() => setActiveTab('file')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all',
                  activeTab === 'file'
                    ? 'bg-telegram text-white shadow-glow'
                    : 'text-surface-600 hover:bg-surface-100'
                )}
              >
                <FileCode size={16} />
                {t('import.tab_file')}
              </button>
              <button
                onClick={() => setActiveTab('paste')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all',
                  activeTab === 'paste'
                    ? 'bg-telegram text-white shadow-glow'
                    : 'text-surface-600 hover:bg-surface-100'
                )}
              >
                <Clipboard size={16} />
                {t('import.tab_paste')}
              </button>
            </div>

            {/* File Upload Tab */}
            {activeTab === 'file' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Drop Zone */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={clsx(
                    'border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all',
                    isDragging
                      ? 'border-telegram bg-telegram/5 scale-105'
                      : 'border-surface-300 bg-white hover:border-telegram/50 hover:bg-telegram/5'
                  )}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".html,.htm"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleFileRead(e.target.files[0])}
                  />
                  <div className="text-5xl mb-4">📄</div>
                  <div className="font-bold text-surface-900 text-lg mb-2">
                    {isDragging ? t('import.drop_here') : t('import.drag_drop')}
                  </div>
                  <div className="text-surface-500 text-sm mb-6">{t('import.or_click')}</div>
                  <div className="btn-primary btn-md inline-flex">
                    <Upload size={16} />
                    {t('import.choose_file')}
                  </div>
                  <div className="text-surface-400 text-xs mt-4">{t('import.file_format')}</div>
                </div>

                {/* Gemini CTA */}
                <div className="card p-5 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Sparkles size={20} className="text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-surface-900 mb-1">{t('import.no_html_title')}</h3>
                      <p className="text-surface-600 text-sm mb-3">{t('import.no_html_sub')}</p>
                      <a
                        href="https://gemini.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn bg-purple-600 text-white btn-sm hover:bg-purple-700"
                      >
                        <Sparkles size={14} />
                        {t('import.gemini_cta')}
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Paste Tab */}
            {activeTab === 'paste' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {fileName && (
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-700">
                    <FileCode size={14} />
                    <span className="font-medium">{fileName}</span>
                    <button onClick={() => { setHtmlInput(''); setFileName(null); setValidation(null); }} className="ml-auto">
                      <X size={14} />
                    </button>
                  </div>
                )}

                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-surface-700">{t('import.html_code')}</label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-surface-400">{sizeKb}KB</span>
                      <button onClick={handlePasteFromClipboard} className="btn-outline btn-sm text-xs">
                        <Clipboard size={12} />
                        {t('import.paste_clipboard')}
                      </button>
                      {htmlInput && (
                        <button
                          onClick={() => { setHtmlInput(''); setValidation(null); }}
                          className="btn-outline btn-sm text-xs text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <X size={12} />
                          {t('import.clear')}
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea
                    value={htmlInput}
                    onChange={(e) => handleHtmlChange(e.target.value)}
                    placeholder={`<!DOCTYPE html>\n<html lang="ms">\n<head>\n  <meta charset="UTF-8">\n  <title>Laman Saya</title>\n</head>\n<body>\n  <h1>Hello World!</h1>\n</body>\n</html>`}
                    className="code-editor w-full"
                    style={{ minHeight: '320px' }}
                    spellCheck={false}
                  />
                  {isValidating && (
                    <div className="absolute top-10 right-3 flex items-center gap-2 bg-surface-800 rounded-lg px-3 py-1.5 text-xs text-surface-300">
                      <RefreshCw size={12} className="animate-spin" />
                      {t('import.validating')}
                    </div>
                  )}
                </div>

                {/* Security Result */}
                <AnimatePresence>
                  {validation && !isValidating && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                    >
                      <SecurityBadge result={validation} t={t} />

                      {(validation.warnings.length > 0 || validation.blockedItems.length > 0) && (
                        <div className="mt-3 card p-4 space-y-2">
                          <div className="text-sm font-semibold text-surface-700 flex items-center gap-2">
                            <Shield size={14} />
                            {t('import.report')}
                          </div>
                          {validation.blockedItems.map((item, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-yellow-700">
                              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                              {item}
                            </div>
                          ))}
                          {validation.warnings.map((w, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-blue-700">
                              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                              {w}
                            </div>
                          ))}
                          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-surface-100">
                            <div className="text-center">
                              <div className="font-bold text-surface-900">{validation.securityReport.linksScanned}</div>
                              <div className="text-xs text-surface-500">{t('import.links_scanned')}</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-surface-900">{validation.securityReport.assetsScanned}</div>
                              <div className="text-xs text-surface-500">{t('import.assets_scanned')}</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-surface-900">{Math.round(validation.securityReport.finalSizeBytes / 1024)}KB</div>
                              <div className="text-xs text-surface-500">{t('import.final_size')}</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </div>

          {/* ─── Right Panel: Actions & Info ─────────────────────── */}
          <div className="space-y-4">

            {/* Security Info Card */}
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Shield size={18} className="text-telegram" />
                <h3 className="font-bold text-surface-900">{t('import.security_check')}</h3>
              </div>
              <div className="space-y-2.5">
                {[
                  t('import.check_xss'),
                  t('import.check_handlers'),
                  t('import.check_links'),
                  t('import.check_crypto'),
                  t('import.check_iframe'),
                  t('import.check_size'),
                  t('import.check_csp'),
                ].map((check) => (
                  <div key={check} className="flex items-center gap-2 text-sm text-surface-600">
                    <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                    {check}
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            {htmlInput && (
              <div className="card p-5">
                <h3 className="font-bold text-surface-900 mb-3 text-sm">{t('import.stats')}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-surface-500">{t('import.size')}</span>
                    <span className={clsx('font-medium', sizeKb > 450 ? 'text-red-600' : sizeKb > 300 ? 'text-yellow-600' : 'text-green-600')}>
                      {sizeKb}KB / 512KB
                    </span>
                  </div>
                  <div className="w-full bg-surface-200 rounded-full h-1.5">
                    <div
                      className={clsx('h-1.5 rounded-full transition-all', sizeKb > 450 ? 'bg-red-500' : sizeKb > 300 ? 'bg-yellow-500' : 'bg-green-500')}
                      style={{ width: `${Math.min(100, (sizeKb / 512) * 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-500">{t('import.lines')}</span>
                    <span className="font-medium text-surface-700">{htmlInput.split('\n').length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-surface-500">{t('import.chars')}</span>
                    <span className="font-medium text-surface-700">{htmlInput.length.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {htmlInput && (
                <button onClick={() => setShowPreview(true)} className="btn-outline btn-md w-full">
                  <Eye size={16} />
                  {t('import.preview_html')}
                </button>
              )}
              <button
                onClick={handleProceed}
                disabled={!htmlInput.trim() || (validation ? !validation.passed : false)}
                className="btn-primary btn-lg w-full group"
              >
                <Zap size={18} />
                {t('import.continue')}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => navigate('/gallery')} className="btn-secondary btn-md w-full">
                {t('import.use_template')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Preview Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {showPreview && htmlInput && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-surface-200">
                <div className="flex items-center gap-2">
                  <Eye size={18} className="text-telegram" />
                  <span className="font-bold text-surface-900">{t('import.preview_html')}</span>
                  {fileName && <span className="badge-gray text-xs">{fileName}</span>}
                </div>
                <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-surface-100 rounded-lg">
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-hidden bg-surface-100 rounded-b-2xl">
                <iframe
                  srcDoc={htmlInput}
                  sandbox="allow-same-origin"
                  className="w-full h-full border-0"
                  style={{ minHeight: '480px' }}
                  title="HTML Preview"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
